// @flow
import React, {useEffect, useState, useRef} from "react";
import {useHistory} from "react-router-dom";
import {useObserver} from "mobx-react-lite";
import {
    EditOutlined,
    CheckOutlined,
    CloseOutlined,
    LockOutlined,
    ExclamationCircleOutlined
} from "@ant-design/icons";
import moment from "moment";
import {
    Descriptions,
    Col,
    Skeleton,
    Row,
    Button,
    Table,
    Tabs,
    Timeline,
    Popover,
    Input,
    Tooltip,
    Tag
} from "antd";
import locale from "antd/es/date-picker/locale/tr_TR";
import {useTranslation} from "react-i18next";

import placeholder from "../../../public/images/person-placeholder.jpg";
import {useFetch} from "../../../hooks/useFetch";
import {useStore} from "../../../hooks/useStore";
import {useWindowSize} from "../../../hooks/useWindowSize";
import useIsFocused from "../../../hooks/useIsFocused";

import {
    Img,
    TimelineCard,
    LeftButton,
    PastTabContainer,
    DateRangePicker,
    TableContainer,
    TimeLineDuration,
    DescriptionContainer,
    ColoredLine,
    TabsContainer,
    TopContainer,
    EditButton,
    SelectContainer,
    CourierPersonalInfoRow
} from "./courierDetailStyles";
import {CourierStatusUpdateModal} from "../../_defaultComponents/CourierStatusUpdateModal/CourierStatusUpdateModal";
import {ChangePasswordModal} from "../../_defaultComponents/ChangePasswordModal/ChangePasswordModal";
import {CustomButton} from "../../_defaultComponents/CustomButton/CustomButton";
import {MultipleSelect} from "../../_defaultComponents/MultipleSelect/MultipleSelect";
import {SelectTitle} from "../../_defaultComponents/MultipleSelect/multipleSelectStyles";

import {
    deliveryStatuses,
    courierStatusColors,
    allowChangeStatuses,
    courierStatuses
} from "../../../constants/constants";
import {ConditionalTooltip} from "../../_defaultComponents/ConditionalTooltip/ConditionalTooltip";
import {
    formatPhoneNumber,
    isConstantExist,
    getConstant,
    calculateDuration
} from "../../../utils/helpers";
import {LogTable} from "./courierLogTable";
import ProductTable from "../../_defaultComponents/ProductTable/ProductTable";
import {ShiftTable} from "./courierShiftTable";
import JSONInput from "react-json-editor-ajrm";
import {CourierDetailMap} from "./CourierDetailMap";
import {CourierWalletTab} from "./courierWalletTab/CourierWalletTab";
import {CourierOnboardingTab} from "./courierOnboardingTab/CourierOnboardingTab";
import {EmptyMessage} from "../../_defaultComponents/EmptyMessage/EmptyMessage";
import {CourierCodPaymentTab} from "./courierCodPaymentTab/CourierCodPaymentTab";
import {CourierInfoRow} from "./courierInfoRow/CourierInfoRow";
import {CourierOnboardingActionButtons} from "./courierOnboardingActionButtons/CourierOnboardingActionButtons";

const {Column} = Table;

export const CourierDetail = ({courierId}) => {
    const stores = useStore();
    const history = useHistory();
    const windowSize = useWindowSize();
    const isFocused = useIsFocused();
    const {t, i18n} = useTranslation([
        "courierDetail",
        // "changeFileUploadList",
        "mapCourierStatuses",
        "courierTypes",
        "deliveryStatuses",
        "orderStatuses",
        "courierStatuses",
        "paymentTypes",
        "courierLogTable",
        "onboardingStatuses",
        "pools"
    ]);

    const [courierStatus, setCourierStatus] = useState("");
    const [courierSelectData, setCourierSelectData] = useState();
    const [courierLogs, setCourierLogs] = useState(null);
    const [dateRange, setDateRange] = useState([]);
    const [currentDelivery, setCurrentDelivery] = useState(null);
    const [tabKey, setTabKey] = useState("courierInformationTab");
    const [recall, setRecall] = useState(true);
    const [visibleStatusModal, setVisibleStatusModal] = useState(false);
    const [visibleChangePasswordModal, setVisibleChangePasswordModal] = useState(false);
    // const [visibleFileUploadModal, setVisibleFileUploadModal] = useState(false);
    const [orderInfoVisible, setOrderInfoVisible] = useState({});
    const [disableEditDedication, setDisableEditDedication] = useState(true);
    const recallRef = useRef(recall);
    recallRef.current = recall;
    const [pools, setPools] = useState([]);
    const [loading, courierData, setData, setLoading] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.courierStore.getCourier.bind(null, {
            courierId
        }),
        dependencies: [recall]
    });

    const [
        currentDeliveryloading,
        currentDeliveryData,
        setcurrentDelivery,
        setcurrentDeliveryLoading
    ] = useFetch({
        defaultDataValue: null,
        fetchFn: stores.courierStore.getCurrentDelivery.bind(null, {
            courierId
        }),
        dependencies: [recall]
    });

    const [
        dedicationDataLoading,
        courierDedicationData,
        setCourierDedicationData,
        setDedicationDataLoading
    ] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.courierStore.courierDedicationLog.bind(null, {
            courierId
        }),
        dependencies: [recall],
        initialCall: !stores.meStore.checkRole("isCompanyUser")
    });

    useEffect(() => {
        if (isFocused) {
            let timeout = null;
            if (tabKey === "courierLatestStatusTab") {
                timeout = setTimeout(() => {
                    setRecall(!recallRef.current);
                }, 2000);
            } else {
                clearTimeout(timeout);
                timeout = null;
            }
            return () => clearTimeout(timeout);
        }
    }, [tabKey, recall, isFocused]);

    const loadPools = async () => {
        const poolIds = courierData?.data?.location?.dedicatedPoolIds;
        if (poolIds && Array.isArray(poolIds) && poolIds.length > 0) {
            const promises = await Promise.allSettled(
                [...poolIds].map(async (data, i) => {
                    return await stores.poolStore.getPool({poolId: data});
                })
            );
            setPools(promises.map(x => x?.value));
        }
    };
    useEffect(() => {
        if (courierData.status && courierData.status !== 200) {
            stores.appStore.openErrorModal({
                content: courierData.data.message,
                onOk: () => {
                    history.push("/couriers");
                }
            });
        }

        let statusValue = courierData?.data?.location?.status;
        if (statusValue === "ready") {
            statusValue = "available";
        }
        if (courierData?.data?.location?.deliveryState) {
            if (courierData?.data?.location?.deliveryState.includes("PickUp")) {
                statusValue = "busyOnPickup";
            }
            if (courierData?.data?.location?.deliveryState.includes("Delivery")) {
                statusValue = "busyOnDelivery";
            }
            if (courierData?.data?.location?.deliveryState.includes("Return")) {
                statusValue = "busyOnReturn";
            }
            if (courierData?.data?.location?.deliveryState.includes("Transfer")) {
                statusValue = "busyOnTransfer";
            }
        }
        const poolIds = courierData?.data?.location?.dedicatedPoolIds;
        if (poolIds && Array.isArray(poolIds)) {
            loadPools();
        }

        setCourierStatus(statusValue || "unavailable");
    }, [courierData]);

    useEffect(() => {
        setCurrentDelivery(currentDeliveryData);
    }, [currentDeliveryData]);

    useEffect(() => {
        if (stores.meStore.checkRole("isCompanyUser")) return;
        setInitialCourierLogs();
    }, []);

    const setInitialCourierLogs = async () => {
        let start = new Date(Date.now() - 1000 * 60 * 60 * 24 * 2);
        start.setHours(0, 0, 0, 0);
        const result = await stores.courierStore.getCourierStatusLogs({
            courierId,
            startDate: start,
            endDate: new Date()
        });
        setCourierLogs(result);
    };

    const updateStatus = async values => {
        const {note, status} = values;
        if (allowChangeStatuses.has(courierStatus)) {
            await stores.courierStore.updateStatus({
                note,
                status: status === "available" ? courierData.data.defaultStatus : status,
                courierId
            });
            await setRecall(!recallRef.current);
            await setVisibleStatusModal(!visibleStatusModal);
        } else {
            stores.appStore.openErrorModal({
                content: t("updateCourierStatusWarning", {ns: "courierDetail"}),
                onOk: () => {
                    setVisibleStatusModal(!visibleStatusModal);
                }
            });
        }
    };

    const updateCourierPassword = async values => {
        const {newPassword, passwordConfirm} = values;
        await setRecall(!recallRef.current);
        setVisibleChangePasswordModal(!visibleChangePasswordModal);
        await stores.courierStore.changePassword({
            newPassword,
            courierId
        });
    };

    const updateCourierDisable = () => {
        stores.appStore.openConfirmModal({
            title: t("modal.confirmTitle", {ns: "common"}),
            content: t("modal.updateCourierDisable.content", {
                ns: "courierDetail",
                disabledStatus: courierData.data.disabled
                    ? t("other.activeLowercase", {ns: "common"})
                    : t("other.inactiveLowercase", {ns: "common"})
            }),
            onOk: async () => {
                await setLoading(true);
                const updatedCourier = await stores.courierStore.updateCourier({
                    courier: {
                        ...courierData.data,
                        disabled: !courierData.data.disabled,
                        photo: courierData.data.photoId
                    },
                    isDisableUpdate: true
                });
                if (updatedCourier) {
                    setRecall(!recallRef.current);
                }
                await setLoading(false);
            }
        });
    };

    const onChangeMultipleSelect = e => {
        setCourierSelectData(e);
    };
    return useObserver(() => (
        <>
            <CourierInfoRow courier={courierData?.data || {}} />
            <TabsContainer
                data-testid="curierDetailScreen"
                activeKey={tabKey}
                onChange={key => {
                    setTabKey(key);
                }}
                tabBarExtraContent={
                    stores.meStore.checkRole("notVisibleForCompanyUser") &&
                    courierData.data && (
                        <Row
                            type="flex"
                            data-testid="courierActionButtons"
                            style={{columnGap: "8px", rowGap: "8px"}}
                        >
                            <Col className="courierDetailActionButtons">
                                {stores.meStore.checkRole("updateCourier") && (
                                    <LeftButton
                                        type="primary"
                                        href={"/couriers/edit/" + courierId}
                                        data-testid="courierUpdateButton"
                                        onClick={event => {
                                            event.preventDefault();
                                            history.push("/couriers/edit/" + courierId);
                                        }}
                                        icon={<EditOutlined />}
                                    >
                                        {t("actions.updateCourier", {ns: "courierDetail"})}
                                    </LeftButton>
                                )}{" "}
                                {/*  {stores.meStore.checkRole("updateCourier") && (
                                    <LeftButton
                                        type="primary"
                                        onClick={() => {
                                            setVisibleChangePasswordModal(true);
                                        }}
                                        icon={<LockOutlined />}
                                    >
                                        {t("actions.updateCourierPassword", {
                                            ns: "courierDetail"
                                        })}
                                    </LeftButton>
                                )} */}
                            </Col>
                            <Col className="courierDetailActionButtons">
                                {stores.meStore.checkRole("updateCourier") && (
                                    <Tooltip
                                        title={
                                            !courierData.data
                                                ? "Kurye bulunamadı"
                                                : !["approved"].includes(
                                                      courierData.data.onboardingStatus
                                                  )
                                                ? "Kurye henüz onaylanmamış"
                                                : ""
                                        }
                                    >
                                        <LeftButton
                                            disabled={
                                                !courierData.data ||
                                                !["approved"].includes(
                                                    courierData.data.onboardingStatus
                                                )
                                            }
                                            type={"primary"}
                                            danger
                                            data-testid="courierActiveInActiveButton"
                                            data-testvalue={
                                                courierData.data.disabled ? "inactive" : "active"
                                            }
                                            onClick={() => {
                                                updateCourierDisable();
                                            }}
                                            icon={
                                                courierData.data.disabled ? (
                                                    <CheckOutlined />
                                                ) : (
                                                    <CloseOutlined />
                                                )
                                            }
                                        >
                                            {courierData.data.disabled
                                                ? t("actions.activate", {ns: "common"})
                                                : t("actions.inactivate", {ns: "common"})}
                                        </LeftButton>
                                    </Tooltip>
                                )}
                            </Col>
                            {stores.meStore.checkRole("listCourierOnboarding") && (
                                <Col>
                                    <CourierOnboardingActionButtons
                                        courier={courierData?.data}
                                        reloadCourier={() => setRecall(!recallRef.current)}
                                        isTabActive={true}
                                        courierLoading={loading}
                                    />
                                </Col>
                            )}
                        </Row>
                    )
                }
            >
                <Tabs.TabPane
                    tab={<span data-testid="courierInformationTab">{"Kurye Detay"}</span>}
                    key="courierInformationTab"
                >
                    {!courierData.data ? (
                        <Skeleton paragraph={{rows: 10, width: "100%"}} active={true}></Skeleton>
                    ) : (
                        <>
                            <ChangePasswordModal
                                title={t("courierResetPasswordModalTitle", {
                                    ns: "courierResetPasswordModal"
                                })}
                                isVisible={visibleChangePasswordModal}
                                updateCourierPassword={values => updateCourierPassword(values)}
                                setVisible={() => {
                                    setVisibleChangePasswordModal(!visibleChangePasswordModal);
                                }}
                            />
                            <CourierStatusUpdateModal
                                isVisible={visibleStatusModal}
                                setVisible={() => {
                                    setVisibleStatusModal(!visibleStatusModal);
                                }}
                                unavailable
                                defaultStatus={courierData.data?.location?.status || ""}
                                courierId={courierId}
                                hasDelivery={!!currentDelivery}
                                updateStatus={values => updateStatus(values)}
                            />

                            <Descriptions
                                column={{xxl: 4, xl: 3, lg: 3, md: 3, sm: 2, xs: 1}}
                                title={
                                    <>
                                        <TopContainer
                                            justify="space-around"
                                            data-testid="courierCompanyDataCard"
                                        >
                                            <Col span={24} sm={10} md={8} lg={4} xl={7} xxl={7}>
                                                <Img
                                                    style={{
                                                        width: "auto",
                                                        objectFit: "cover",
                                                        aspectRatio: "1/1"
                                                    }}
                                                    data-testid="courierPhoto"
                                                    src={
                                                        courierData?.data?.documents?.profilePicture
                                                            ?.publicUrl ||
                                                        courierData.data.photo ||
                                                        placeholder
                                                    }
                                                />
                                                <Row
                                                    justify="center"
                                                    data-testid="courierName"
                                                    style={{marginBottom: "16px"}}
                                                >
                                                    {courierData.data.fullname}
                                                </Row>
                                                <CourierPersonalInfoRow>
                                                    <Tag>
                                                        Vatandaşlık Durumu :{" "}
                                                        <b style={{marginLeft: "4px"}}>
                                                            {courierData.data.isCitizen
                                                                ? "T.C. Vatandaşı"
                                                                : "Yabancı"}
                                                        </b>
                                                    </Tag>
                                                </CourierPersonalInfoRow>
                                                <CourierPersonalInfoRow>
                                                    <Tag>
                                                        Kimlik Numarası :{" "}
                                                        <b style={{marginLeft: "4px"}}>
                                                            {courierData.data.tckn}
                                                        </b>
                                                    </Tag>
                                                </CourierPersonalInfoRow>
                                                <CourierPersonalInfoRow>
                                                    <Tag>
                                                        Telefon :{" "}
                                                        <b style={{marginLeft: "4px"}}>
                                                            {formatPhoneNumber(
                                                                courierData.data.cellPhone
                                                            )}
                                                        </b>
                                                    </Tag>
                                                </CourierPersonalInfoRow>
                                                <CourierPersonalInfoRow>
                                                    <Tag>
                                                        Doğum Tarihi :{" "}
                                                        <b style={{marginLeft: "4px"}}>
                                                            {courierData?.data?.birthDate
                                                                ? moment(courierData.data.birthDate)
                                                                      .locale(
                                                                          i18n.resolvedLanguage ||
                                                                              "tr"
                                                                      )
                                                                      .format("ll")
                                                                : "-"}
                                                        </b>
                                                    </Tag>
                                                </CourierPersonalInfoRow>
                                                <Row justify="center">
                                                    <CourierPersonalInfoRow>
                                                        <Tag>
                                                            Sürücü Belgesi :{" "}
                                                            <b style={{marginLeft: "4px"}}>
                                                                {courierData.data.drivingLicenses
                                                                    .map(
                                                                        drivingLicense =>
                                                                            drivingLicense?.label.tr
                                                                    )
                                                                    .join(", ") || "-"}
                                                            </b>
                                                        </Tag>
                                                    </CourierPersonalInfoRow>
                                                    <CourierPersonalInfoRow>
                                                        <Tag>
                                                            Cinsiyet :{" "}
                                                            <b style={{marginLeft: "4px"}}>
                                                                {courierData?.data?.gender
                                                                    ? t(
                                                                          `fields.${courierData.data.gender}`,
                                                                          {
                                                                              ns: "common"
                                                                          }
                                                                      )
                                                                    : "-"}
                                                            </b>
                                                        </Tag>
                                                    </CourierPersonalInfoRow>
                                                    <CourierPersonalInfoRow>
                                                        <Tag>
                                                            {t("fields.bloodGroup", {ns: "common"})}{" "}
                                                            :{" "}
                                                            <b style={{marginLeft: "4px"}}>
                                                                {courierData?.data?.bloodGroup ||
                                                                    "-"}
                                                            </b>
                                                        </Tag>
                                                    </CourierPersonalInfoRow>
                                                </Row>
                                                <CourierPersonalInfoRow>
                                                    <Tag>
                                                        P1* Belgesi Durumu :
                                                        <b style={{marginLeft: "4px"}}>
                                                            {typeof courierData.data
                                                                .hasP1Certificate === "boolean"
                                                                ? courierData.data.hasP1Certificate
                                                                    ? "Var"
                                                                    : "Yok"
                                                                : "-"}
                                                        </b>
                                                    </Tag>
                                                </CourierPersonalInfoRow>
                                                <CourierPersonalInfoRow>
                                                    <Tag>
                                                        Çalışmak İstenen İl :
                                                        <b style={{marginLeft: "4px"}}>
                                                            {courierData.data.preferredCity?.name ||
                                                                "-"}
                                                        </b>
                                                    </Tag>
                                                </CourierPersonalInfoRow>
                                                <CourierPersonalInfoRow>
                                                    <Tooltip
                                                        title={courierData.data.preferredDistricts
                                                            ?.map(district => district?.name)
                                                            .join(", ")}
                                                    >
                                                        <Tag
                                                            style={{
                                                                maxWidth: "230px",
                                                                whiteSpace: "normal",
                                                                textAlign: "left",
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                                display: "-webkit-box",
                                                                WebkitLineClamp: 3,
                                                                WebkitBoxOrient: "vertical"
                                                            }}
                                                        >
                                                            Çalışmak İstenen İlçe(ler) :<br />
                                                            <b>
                                                                {courierData.data.preferredDistricts
                                                                    ?.map(
                                                                        district => district?.name
                                                                    )
                                                                    .join(", ") || "-"}
                                                            </b>
                                                        </Tag>
                                                    </Tooltip>
                                                </CourierPersonalInfoRow>
                                            </Col>
                                            {stores.meStore.checkRole(
                                                "notVisibleForCompanyUser"
                                            ) && (
                                                <Col
                                                    span={24}
                                                    sm={14}
                                                    md={16}
                                                    lg={8}
                                                    xl={8}
                                                    xxl={8}
                                                >
                                                    <SelectTitle>
                                                        {t("fields.poolRegion", {
                                                            ns: "pools"
                                                        })}
                                                    </SelectTitle>
                                                    <Input
                                                        value={pools?.map(x => x.name).join(", ")}
                                                        disabled={true}
                                                        style={{color: "black"}}
                                                    ></Input>
                                                    <SelectContainer
                                                        align="center"
                                                        justify="center"
                                                        style={{width: "100%"}}
                                                    >
                                                        <Col
                                                            data-testid="courierComponySelectCard"
                                                            style={{width: "100%"}}
                                                        >
                                                            <MultipleSelect
                                                                direction={"vertical"}
                                                                data={courierData.data}
                                                                disabled={disableEditDedication}
                                                                list={[
                                                                    {
                                                                        key: "company",
                                                                        value: t("fields.company", {
                                                                            ns: "common"
                                                                        }),
                                                                        width: "100%"
                                                                    },
                                                                    {
                                                                        key: "region",
                                                                        value: t("fields.region", {
                                                                            ns: "common"
                                                                        }),
                                                                        width: "100%"
                                                                    },
                                                                    {
                                                                        key: "subcompany",
                                                                        value: t(
                                                                            "fields.subcompany",
                                                                            {ns: "common"}
                                                                        ),
                                                                        width: "100%"
                                                                    },
                                                                    {
                                                                        key:
                                                                            "deliveryPointRegionGroup",
                                                                        value: t("fields.region", {
                                                                            ns: "common"
                                                                        }),
                                                                        width: "100%"
                                                                    },
                                                                    {
                                                                        key: "slotCourier",
                                                                        value: t(
                                                                            "fields.slotCourier",
                                                                            {ns: "common"}
                                                                        ),
                                                                        width: "100%"
                                                                    }
                                                                ]}
                                                                onChange={onChangeMultipleSelect}
                                                            />
                                                        </Col>
                                                    </SelectContainer>
                                                    {stores.meStore.checkRole("assignCourier") && (
                                                        <Row justify="end">
                                                            {disableEditDedication ? (
                                                                <EditButton
                                                                    type="primary"
                                                                    data-testid="editButton"
                                                                    onClick={() =>
                                                                        setDisableEditDedication(
                                                                            false
                                                                        )
                                                                    }
                                                                    icon={<EditOutlined />}
                                                                >
                                                                    {t("actions.edit", {
                                                                        ns: "common"
                                                                    })}
                                                                </EditButton>
                                                            ) : (
                                                                <Button
                                                                    type="danger"
                                                                    data-testid="saveCompanyInformationButton"
                                                                    onClick={() => {
                                                                        stores.appStore.openConfirmModal(
                                                                            {
                                                                                title: t(
                                                                                    "modal.confirmTitle",
                                                                                    {
                                                                                        ns: "common"
                                                                                    }
                                                                                ),
                                                                                content: t(
                                                                                    "modal.wantToChangeDedication",
                                                                                    {
                                                                                        ns:
                                                                                            "courierDetail"
                                                                                    }
                                                                                ),
                                                                                okButtonProps: {
                                                                                    "data-testid":
                                                                                        "sureButton"
                                                                                },
                                                                                cancelButtonProps: {
                                                                                    "data-testid":
                                                                                        "cancelButton"
                                                                                },
                                                                                onOk: async () => {
                                                                                    setCourierSelectData();
                                                                                    setDisableEditDedication(
                                                                                        true
                                                                                    );
                                                                                    await stores.courierStore.courierUpdateDedication(
                                                                                        {
                                                                                            ...courierSelectData,
                                                                                            courierId
                                                                                        }
                                                                                    );
                                                                                    await setRecall(
                                                                                        !recallRef.current
                                                                                    );
                                                                                }
                                                                            }
                                                                        );
                                                                    }}
                                                                    icon={<CheckOutlined />}
                                                                    disabled={!courierSelectData}
                                                                >
                                                                    {t("actions.save", {
                                                                        ns: "common"
                                                                    })}
                                                                </Button>
                                                            )}
                                                        </Row>
                                                    )}
                                                </Col>
                                            )}
                                            <Col span={24} sm={24} md={24} lg={8} xl={8} xxl={8}>
                                                <CourierDetailMap courier={courierData?.data} />
                                            </Col>
                                        </TopContainer>
                                    </>
                                }
                                bordered
                            ></Descriptions>
                            <Row type="flex" style={{marginTop: "16px", marginBottom: "16px"}}>
                                <Col span={24}>
                                    <CourierOnboardingTab
                                        courier={courierData.data}
                                        courierLoading={loading}
                                        reloadCourier={() => setRecall(!recallRef.current)}
                                    />
                                </Col>
                            </Row>
                            <Row type="flex" justify="end"></Row>
                        </>
                    )}
                </Tabs.TabPane>

                {stores.meStore.checkRole("listCourierShifts") && (
                    <Tabs.TabPane
                        key="shiftsTab"
                        tab={<span>{t("tabs.shifts", {ns: "courierDetail"})}</span>}
                    >
                        <ShiftTable courierId={courierId} />
                    </Tabs.TabPane>
                )}
                {stores.meStore.checkRole("listCourierHistory") && (
                    <Tabs.TabPane
                        key="historyTab"
                        tab={<span data-testid="historyTab">{"Durum Geçmişi"}</span>}
                    >
                        {!courierData.data ? (
                            <Skeleton
                                paragraph={{rows: 10, width: "100%"}}
                                active={true}
                            ></Skeleton>
                        ) : (
                            <PastTabContainer>
                                <Row
                                    type="flex"
                                    gutter={[0, 10]}
                                    style={{justifyContent: "space-between"}}
                                >
                                    <Col>
                                        <h3 data-testid="courierName">
                                            {courierData.data.fullname}
                                        </h3>
                                    </Col>
                                    {stores.meStore.checkRole("updateCourierStatus") && (
                                        <ConditionalTooltip
                                            condition={!allowChangeStatuses.has(courierStatus)}
                                            text={t("updateCourierStatusWarning", {
                                                ns: "courierDetail"
                                            })}
                                            data-testid="curierStatusUpdateButton"
                                            child={
                                                <LeftButton
                                                    type="primary"
                                                    onClick={() => {
                                                        setVisibleStatusModal(true);
                                                    }}
                                                    disabled={
                                                        !allowChangeStatuses.has(courierStatus)
                                                    }
                                                    icon={<EditOutlined />}
                                                >
                                                    {t("actions.updateCourierStatus", {
                                                        ns: "courierDetail"
                                                    })}
                                                </LeftButton>
                                            }
                                        />
                                    )}
                                </Row>
                                <Row gutter={[20, 20]}>
                                    <Col>
                                        <DateRangePicker
                                            locale={locale}
                                            onChange={dates => {
                                                const updatedDate = [...dateRange];
                                                updatedDate[0] = dates;
                                                setDateRange(updatedDate);
                                            }}
                                            format={"DD/MM/YYYY"}
                                            placeholder={t("fields.startDate", {ns: "common"})}
                                            data-testid="startDate"
                                        />
                                    </Col>
                                    <Col>
                                        <DateRangePicker
                                            locale={locale}
                                            onChange={dates => {
                                                const updatedDate = [...dateRange];
                                                updatedDate[1] = dates;
                                                setDateRange(updatedDate);
                                            }}
                                            format={"DD/MM/YYYY"}
                                            placeholder={t("fields.endDate", {ns: "common"})}
                                            data-testid="endDate"
                                        />
                                    </Col>
                                    <Col>
                                        <CustomButton
                                            icon={<CheckOutlined />}
                                            type="primary"
                                            data-testid="submitButton"
                                            onClick={async () => {
                                                let start;
                                                let end;
                                                if (dateRange) {
                                                    [start, end] = dateRange;
                                                }
                                                const result = await stores.courierStore.getCourierStatusLogs(
                                                    {
                                                        courierId,
                                                        startDate: dateRange[0]
                                                            ? new Date(start)
                                                            : null,
                                                        endDate: dateRange[1] ? new Date(end) : null
                                                    }
                                                );
                                                setCourierLogs(result);
                                            }}
                                        />
                                    </Col>
                                </Row>
                                <Row gutter={[20, 20]}>
                                    <Col span={24}>
                                        {t("historySearchTitle", {ns: "courierDetail"})}
                                    </Col>
                                    {courierLogs !== null &&
                                        (!courierLogs.length ? (
                                            <Col span={24}>
                                                <EmptyMessage
                                                    descriptionKey="noInfoToDisplay"
                                                    ns="courierDetail"
                                                />
                                            </Col>
                                        ) : (
                                            <Col span={24}>
                                                <Timeline mode="left" data-testid="timeline">
                                                    {courierLogs.map((log, cardIndex) =>
                                                        log.status ? (
                                                            <Timeline.Item
                                                                key={cardIndex}
                                                                color={
                                                                    courierStatusColors[log.status]
                                                                }
                                                                label={
                                                                    <TimelineCard
                                                                        right
                                                                        data-testid="TimelineCard"
                                                                    >
                                                                        <span data-testid="timelineText">
                                                                            {moment(
                                                                                new Date(
                                                                                    log.createdAt
                                                                                )
                                                                            ).format("L LTS")}{" "}
                                                                            {log?.gpsProvider && (
                                                                                <Popover
                                                                                    content={
                                                                                        <JSONInput
                                                                                            id="courierLogGPSProviderJSON"
                                                                                            placeholder={
                                                                                                log?.gpsProvider
                                                                                            }
                                                                                            theme="light_mitsuketa_tribute"
                                                                                            width="auto"
                                                                                            height="auto"
                                                                                            viewOnly={
                                                                                                true
                                                                                            }
                                                                                            confirmGood={
                                                                                                false
                                                                                            }
                                                                                        />
                                                                                    }
                                                                                >
                                                                                    <ExclamationCircleOutlined />
                                                                                </Popover>
                                                                            )}
                                                                        </span>
                                                                        <TimeLineDuration
                                                                            data-testid="TimelineDuration"
                                                                            style={{
                                                                                fontWeight: "bold",
                                                                                fontSize: 13
                                                                            }}
                                                                        >
                                                                            {courierLogs[
                                                                                cardIndex - 1
                                                                            ] &&
                                                                                t("duration", {
                                                                                    ns:
                                                                                        "courierDetail"
                                                                                }) +
                                                                                    ": " +
                                                                                    calculateDuration(
                                                                                        {
                                                                                            startDate:
                                                                                                log.createdAt,
                                                                                            endDate:
                                                                                                courierLogs[
                                                                                                    cardIndex -
                                                                                                        1
                                                                                                ]
                                                                                                    .createdAt,
                                                                                            dayUnit: t(
                                                                                                "units.day.short",
                                                                                                {
                                                                                                    ns:
                                                                                                        "common"
                                                                                                }
                                                                                            ),
                                                                                            hourUnit: t(
                                                                                                "units.hour.short",
                                                                                                {
                                                                                                    ns:
                                                                                                        "common"
                                                                                                }
                                                                                            ),
                                                                                            minuteUnit: t(
                                                                                                "units.minute.short",
                                                                                                {
                                                                                                    ns:
                                                                                                        "common"
                                                                                                }
                                                                                            ),
                                                                                            secondUnit: t(
                                                                                                "units.second.short",
                                                                                                {
                                                                                                    ns:
                                                                                                        "common"
                                                                                                }
                                                                                            )
                                                                                        }
                                                                                    )}
                                                                        </TimeLineDuration>
                                                                    </TimelineCard>
                                                                }
                                                            >
                                                                <ColoredLine
                                                                    data-testid="coloredLine"
                                                                    color={
                                                                        courierStatusColors[
                                                                            log.status
                                                                        ]
                                                                    }
                                                                />
                                                                <DescriptionContainer
                                                                    color={
                                                                        courierStatusColors[
                                                                            log.status
                                                                        ]
                                                                    }
                                                                    bordered
                                                                    column={1}
                                                                >
                                                                    <Descriptions.Item>
                                                                        <span data-testid="courierStatuses">
                                                                            {isConstantExist(
                                                                                courierStatuses,
                                                                                log.status
                                                                            )
                                                                                ? t(
                                                                                      getConstant(
                                                                                          courierStatuses,
                                                                                          log.status
                                                                                      )
                                                                                          .translation_key,
                                                                                      {
                                                                                          ns:
                                                                                              "courierStatuses"
                                                                                      }
                                                                                  )
                                                                                : "-"}
                                                                        </span>
                                                                    </Descriptions.Item>
                                                                    {log.reason && (
                                                                        <Descriptions.Item
                                                                            label={t("logReason", {
                                                                                ns: "courierDetail"
                                                                            })}
                                                                        >
                                                                            <span data-testid="reason">
                                                                                {log.reason || "-"}
                                                                            </span>
                                                                        </Descriptions.Item>
                                                                    )}
                                                                    {log.details?.mood && (
                                                                        <Descriptions.Item
                                                                            label={t("logMood", {
                                                                                ns: "courierDetail"
                                                                            })}
                                                                        >
                                                                            <span data-testid="mood">
                                                                                {log.details.mood.toUpperCase()}
                                                                            </span>
                                                                        </Descriptions.Item>
                                                                    )}
                                                                    {log.details?.note && (
                                                                        <Descriptions.Item
                                                                            label={t("logDetail", {
                                                                                ns: "courierDetail"
                                                                            })}
                                                                        >
                                                                            <span data-testid="detailsNote">
                                                                                {log.details.note}
                                                                            </span>
                                                                        </Descriptions.Item>
                                                                    )}
                                                                </DescriptionContainer>
                                                            </Timeline.Item>
                                                        ) : (
                                                            <Timeline.Item
                                                                key={cardIndex}
                                                                label={
                                                                    <TimelineCard
                                                                        data-testid="timelineCard"
                                                                        right
                                                                        title={
                                                                            <span data-testid="createdAt">
                                                                                {moment(
                                                                                    new Date(
                                                                                        log.createdAt
                                                                                    )
                                                                                )
                                                                                    .locale(
                                                                                        i18n.resolvedLanguage ||
                                                                                            "tr"
                                                                                    )
                                                                                    .format(
                                                                                        "ddd, MMM D YYYY LTS"
                                                                                    )}
                                                                            </span>
                                                                        }
                                                                    >
                                                                        <span data-testid="companyName">
                                                                            {
                                                                                log.company
                                                                                    .companyName
                                                                            }
                                                                        </span>
                                                                    </TimelineCard>
                                                                }
                                                            >
                                                                <TimelineCard
                                                                    title={
                                                                        <>
                                                                            <span data-testid="deliveryStatuses">
                                                                                {isConstantExist(
                                                                                    deliveryStatuses,
                                                                                    log.deliveryState
                                                                                )
                                                                                    ? t(
                                                                                          `${
                                                                                              getConstant(
                                                                                                  deliveryStatuses,
                                                                                                  log.deliveryState
                                                                                              )
                                                                                                  .translation_key
                                                                                          }.name`,
                                                                                          {
                                                                                              ns:
                                                                                                  "deliveryStatuses"
                                                                                          }
                                                                                      )
                                                                                    : "-"}
                                                                            </span>
                                                                            {log.deliveryState ===
                                                                                "canceled" && (
                                                                                <h6>
                                                                                    {t(
                                                                                        "cancelReason",
                                                                                        {
                                                                                            ns:
                                                                                                "courierDetail"
                                                                                        }
                                                                                    )}
                                                                                    :
                                                                                    <span data-testid="cancelText">
                                                                                        {
                                                                                            log.cancelText
                                                                                        }
                                                                                    </span>
                                                                                </h6>
                                                                            )}
                                                                        </>
                                                                    }
                                                                >
                                                                    <Popover
                                                                        content={
                                                                            <>
                                                                                <TableContainer
                                                                                    rowKey="_id"
                                                                                    dataSource={
                                                                                        log.orders
                                                                                    }
                                                                                    pagination={
                                                                                        false
                                                                                    }
                                                                                    data-testid="ordersTable"
                                                                                    expandable={{
                                                                                        expandedRowRender: record => {
                                                                                            return (
                                                                                                <ProductTable
                                                                                                    dataSource={
                                                                                                        record.products
                                                                                                    }
                                                                                                />
                                                                                            );
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    <Column
                                                                                        dataIndex="receiverName"
                                                                                        title={t(
                                                                                            "fields.customerName",
                                                                                            {
                                                                                                ns:
                                                                                                    "common"
                                                                                            }
                                                                                        )}
                                                                                        render={text => (
                                                                                            <span data-testid="receiverName">
                                                                                                {
                                                                                                    text
                                                                                                }
                                                                                            </span>
                                                                                        )}
                                                                                    ></Column>
                                                                                    <Column
                                                                                        dataIndex="receiverPhone"
                                                                                        title={t(
                                                                                            "fields.customerPhone",
                                                                                            {
                                                                                                ns:
                                                                                                    "courierDetail"
                                                                                            }
                                                                                        )}
                                                                                        render={text => (
                                                                                            <span data-testid="receiverPhone">
                                                                                                {
                                                                                                    text
                                                                                                }
                                                                                            </span>
                                                                                        )}
                                                                                    ></Column>
                                                                                    <Column
                                                                                        dataIndex="receiverAddress"
                                                                                        title={t(
                                                                                            "fields.customerAddress",
                                                                                            {
                                                                                                ns:
                                                                                                    "courierDetail"
                                                                                            }
                                                                                        )}
                                                                                        render={text => (
                                                                                            <span data-testid="receiverAddress">
                                                                                                {
                                                                                                    text
                                                                                                }
                                                                                            </span>
                                                                                        )}
                                                                                    ></Column>
                                                                                </TableContainer>
                                                                            </>
                                                                        }
                                                                        trigger="click"
                                                                        visible={
                                                                            orderInfoVisible[
                                                                                log._id
                                                                            ] || false
                                                                        }
                                                                        onVisibleChange={visible => {
                                                                            const abc = {};
                                                                            abc[log._id] = visible;
                                                                            setOrderInfoVisible(
                                                                                abc
                                                                            );
                                                                        }}
                                                                    >
                                                                        <a>
                                                                            {log.orders.map(
                                                                                (
                                                                                    order,
                                                                                    orderIndex
                                                                                ) =>
                                                                                    `${
                                                                                        order.orderNumber
                                                                                    }${
                                                                                        log.orders[
                                                                                            orderIndex +
                                                                                                1
                                                                                        ]
                                                                                            ? ", "
                                                                                            : ""
                                                                                    }`
                                                                            )}
                                                                        </a>
                                                                    </Popover>
                                                                </TimelineCard>
                                                            </Timeline.Item>
                                                        )
                                                    )}
                                                </Timeline>
                                            </Col>
                                        ))}
                                </Row>
                            </PastTabContainer>
                        )}
                    </Tabs.TabPane>
                )}
                {stores.meStore.checkRole("listCourierDedicationHistory") && (
                    <Tabs.TabPane
                        key="assignmentHistoryTab"
                        tab={
                            <span data-testid="assignmentHistoryTab">
                                {t("tabs.assignmentHistoryTab", {ns: "courierDetail"})}
                            </span>
                        }
                    >
                        <LogTable loading={dedicationDataLoading} data={courierDedicationData} />
                    </Tabs.TabPane>
                )}
                {stores.meStore.checkRole("listCourierWallet") && (
                    <Tabs.TabPane
                        tab={<span data-testid="courierLatestStatusTab">Cüzdan</span>}
                        key="courierWalletTab"
                    >
                        <CourierWalletTab
                            isTabActive={tabKey === "courierWalletTab"}
                            courier={courierData.data}
                            courierLoading={loading}
                            reloadCourier={() => setRecall(!recallRef.current)}
                        />
                    </Tabs.TabPane>
                )}

                {stores.meStore.checkRole("listCourierCODInfo") && (
                    <Tabs.TabPane
                        tab={<span data-testid="vigoPosTab">VigoPOS</span>}
                        key="vigoPosTab"
                    >
                        <CourierCodPaymentTab
                            courier={courierData.data}
                            courierLoading={loading}
                            reloadCourier={() => setRecall(!recallRef.current)}
                        />
                    </Tabs.TabPane>
                )}
            </TabsContainer>
        </>
    ));
};
